import React from "react";
import fotoSrc from "../../../img/mediakit/lamina03-foto.jpg";
import { getLocale } from "../../../utils/getLocale";
import { useMediakitData } from "../MediakitDataProvider";
import { BanderaChile } from "./Banderas";
import { Foto } from "./Foto";
import styles from "./Lamina03Crecimiento.module.scss";

// Datos vivos (SOLO edificios OPERATIVOS, sin los en instalación): misma fuente única que
// /inventario (InventarioAvanzado), calcularResumenRedComercial
// (src/utils/getResumenRedComercial.js, 10.1) — declarada en mediakitDataSources.js, nunca
// reimplementada acá. Página pública que lee exactamente los mismos datos públicos que /inventario
// ya lee, sin columnas/tablas nuevas (10.279, pre-declarado en el prompt). Wecast es contenido de
// Chile — país fijo, no depende de auth.
// La lámina NUNCA dispara su propio fetch — lee de MediakitDataProvider (montado al abrir el visor
// en la lámina 1). Si llega acá con los datos ya resueltos, se muestra completa de entrada; si no,
// "…" (10.273); si falló, "—" visible (10.177, nunca un número inventado).

// Objetivo 2027 (meta declarada, no un dato vivo) — cada meta referencia el MISMO campo de
// `resumen` que ya muestra la fila HOY correspondiente, así la barra de avance sale del mismo dato
// vivo sin un segundo cálculo (10.1). Constantes en un solo lugar (2026-09-27, definido por
// Ignacio, rediseño "panel v4").
const OBJETIVOS_2027 = [
  { campo: "totalOperativos", meta: 100, etiqueta: "edificios" },
  { campo: "pantallasOperativos", meta: 500, etiqueta: "pantallas" },
  { campo: "empresasOperativos", meta: 2000, etiqueta: "empresas" },
  { campo: "personasOperativos", meta: 130000, etiqueta: "personas" },
];

// Regiones del objetivo 2027 — checklist con estado. Fuente única: cambiar `abierta` a `true` acá
// es lo ÚNICO necesario para marcar una región como abierta en la lámina (2026-09-27, pedido
// explícito de Ignacio).
const REGIONES_2027 = [
  { nombre: "Antofagasta", abierta: false },
  { nombre: "Concepción", abierta: false },
  { nombre: "Iquique", abierta: false },
];

function Lamina03Crecimiento() {
  const { status, data: resumen } = useMediakitData("resumenRed");
  const error = status === "error";

  // Valor crudo (número o null) — para cálculos (% de la barra); nunca "0" mientras carga
  // (10.177/10.273: un 0 real y un "todavía no sé" no son lo mismo).
  const crudo = (campo) => {
    if (error) return null;
    const n = resumen?.[campo];
    return typeof n === "number" ? n : null;
  };
  // Mismo formato con separador de miles que /inventario para estas columnas (toLocaleString +
  // getLocale, 10.1).
  const valor = (campo) => {
    if (error) return "—";
    const n = crudo(campo);
    return n === null ? "…" : n.toLocaleString(getLocale("Chile"));
  };

  return (
    <div className={styles.lamina} data-mk-lamina="crecimiento">
      <Foto src={fotoSrc} className={styles.foto} data-mk="foto" />

      <div className={styles.panel} data-mk="panel">
        <BanderaChile className={styles.banderaChica} data-mk="bandera" />

        <div className={styles.contenido} data-mk="contenido">
          <div className={styles.fila1} data-mk="fila1">
            <div className={styles.celda} data-mk="fila1-celda-1">
              <span className={styles.fila1Numero} data-mk="fila1-numero-1">
                {valor("totalOperativos")}
              </span>
              <span className={styles.fila1Etiqueta} data-mk="fila1-etiqueta-1">
                Edificios
              </span>
            </div>
            <div className={styles.celda} data-mk="fila1-celda-2">
              <span className={styles.fila1Numero} data-mk="fila1-numero-2">
                {valor("pantallasOperativos")}
              </span>
              <span className={styles.fila1Etiqueta} data-mk="fila1-etiqueta-2">
                Pantallas
              </span>
            </div>
            <div className={styles.celda} data-mk="fila1-celda-3">
              <span className={styles.fila1Numero} data-mk="fila1-numero-3">
                {valor("empresasOperativos")}
              </span>
              <span className={styles.fila1Etiqueta} data-mk="fila1-etiqueta-3">
                Empresas (B2B)
              </span>
            </div>
          </div>

          <div className={styles.fila2} data-mk="fila2">
            <div className={styles.celda} data-mk="fila2-celda-1">
              <span className={styles.fila2Numero} data-mk="fila2-numero-1">
                {valor("personasOperativos")}
              </span>
              <span className={styles.fila2Etiqueta} data-mk="fila2-etiqueta-1">
                Personas únicas
              </span>
            </div>
            <div className={styles.celda} data-mk="fila2-celda-2">
              <span className={styles.fila2Numero} data-mk="fila2-numero-2">
                {valor("contactosDiariosOperativos")}
              </span>
              <span className={styles.fila2Etiqueta} data-mk="fila2-etiqueta-2">
                Contactos diarios
              </span>
            </div>
          </div>

          <div className={styles.separador} data-mk="separador" />

          <div className={styles.objetivoBloque} data-mk="objetivo">
            <p className={styles.objetivoTitulo}>Objetivo 2027</p>

            <div className={styles.objetivoGrid} data-mk="objetivo-grid">
              {OBJETIVOS_2027.map((o, i) => {
                const actual = crudo(o.campo);
                const pct = actual === null ? 0 : Math.min(100, Math.round((actual / o.meta) * 100));
                return (
                  <div className={styles.objetivoCelda} key={o.campo} data-mk={`objetivo-celda-${i + 1}`}>
                    <span className={styles.objetivoNumero} data-mk={`objetivo-numero-${i + 1}`}>
                      {o.meta.toLocaleString(getLocale("Chile"))}
                    </span>
                    <span className={styles.objetivoEtiqueta} data-mk={`objetivo-etiqueta-${i + 1}`}>
                      {o.etiqueta}
                    </span>
                    <div className={styles.barraFondo} data-mk={`objetivo-barra-${i + 1}`}>
                      <div
                        className={styles.barraRelleno}
                        style={{ width: `${pct}%` }}
                        data-mk={`objetivo-relleno-${i + 1}`}
                      />
                    </div>
                    <span className={styles.objetivoPorcentaje} data-mk={`objetivo-porcentaje-${i + 1}`}>
                      {actual === null ? "…" : `${pct}%`}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className={styles.regionesFila} data-mk="regiones">
              <span className={styles.regionesTitulo}>Regiones</span>
              {REGIONES_2027.map((r, i) => (
                <span className={styles.regionItem} key={r.nombre}>
                  <span
                    className={`${styles.regionCheck} ${r.abierta ? styles.regionCheckAbierta : ""}`}
                    data-mk={`region-check-${i + 1}`}
                  >
                    {r.abierta ? "✓" : ""}
                  </span>
                  {r.nombre}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Lamina03Crecimiento;
